import { isPlatformBrowser } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { createGIF } from 'gifshot';
import { Container, Renderer, RenderTexture } from 'pixi.js';

import { AppComponent } from 'app/app.component';
import { Timeout } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';

import { PHOTO_BOOTH_FRAME_PADDING, FONT_FAMILY_BOLD } from '../constants';
import Message from '../message/message';

import { HeaderToastMessageService } from './header-toast-message.service';
import { SceneService } from './scene.service';

const RESOLUTION = 1;
export interface PhotoBoothPicture {
  url: string;
  id: number;
  isGif?: boolean;
}
export interface FramedPicture {
  id: number;
  base64: string;
  type: string;
  height: number;
  width: number;
}
@Injectable({
  providedIn: 'root',
})
export class PhotoBoothService extends Loggable {
  private headerToastMessageService = inject(HeaderToastMessageService);
  private sceneService = inject(SceneService);

  // Public signals
  public readonly photos = signal<string[]>([]);
  public readonly boothStartedByUser = signal<boolean>(false);
  public readonly boothInSession = signal<boolean>(false);
  public readonly photosCompleted = signal<boolean>(false);

  // Computed values
  public readonly hasPhotos = computed(() => this.photos().length > 0);
  public readonly isBoothActive = computed(() => this.boothInSession() || this.boothStartedByUser());
  public readonly canUsePhotoBooth = computed(() => !this.isBoothActive());

  // Private state
  private countDownResolver?: (value: boolean) => void;
  private countdown?: Timeout;
  private bannerId?: number;

  public constructor() {
    super();
    this.setDisplayName('PhotoBoothService');
    this.photos.set([]);
  }

  public startCountdown(message: string, duration: number, canCancel = true): Promise<boolean> {
    return new Promise((resolve) => {
      this.countDownResolver = resolve;
      let timeLeft = duration;
      this.countdown = setInterval(() => {
        if (timeLeft > 0) {
          this.bannerId = this.headerToastMessageService.pushStaticMessage(
            {
              message: `${message} ${timeLeft} seconds.`,
              buttonText: canCancel ? 'Cancel' : undefined,
              replace: this.bannerId,
            },
            () => {
              if (this.bannerId === 0) {
                this.boothStartedByUser.set(false);
              }

              this.removeBanner();
              this.clearCountdown();
              this.reset();
              resolve(true);
            },
          );
        } else {
          this.boothInSession.set(false);
          this.clearCountdown();
          this.removeBanner();
          resolve(false);
        }
        timeLeft = timeLeft - 1;
      }, 1000);
    });
  }

  public handlePhotoBoothStart(message: Message): void {
    this.boothInSession.set(true);
    const index = message.onlyPlayers?.indexOf(this.sceneService.sceneManager?.player?.userId ?? '');
    if (!index) {
      return;
    }
    if (index >= 0) {
      this.startCountdown(message.message ?? '', message.photoBoothData?.notificationDuration ?? 3, false).catch(
        (e) => {
          this.error('Error while start countdown', e);
        },
      );
    }
  }

  public async takePhoto(background: string | undefined): Promise<void> {
    if (!isPlatformBrowser(AppComponent.PLATFORM_ID)) {
      return;
    }
    const { sceneManager } = this.sceneService;
    if (!sceneManager) {
      return;
    }
    const { screenHeight, screenWidth } = sceneManager.viewport;
    const { renderer, stage } = sceneManager.app as unknown as { renderer: Renderer; stage: Container };

    // Render the stage to a new texture
    const texture = RenderTexture.create({ width: screenWidth, height: screenHeight });
    // TODO: marked as deprecated, update syntax
    renderer.render(stage, { renderTexture: texture });

    // Extract the canvas directly
    const sourceCanvas = renderer.extract.canvas(texture);
    let image: string | undefined;

    if (sourceCanvas && typeof sourceCanvas.toDataURL === 'function') {
      image = sourceCanvas.toDataURL('image/png', RESOLUTION);
    } else {
      this.error('Failed to extract canvas or create data URL');
    }

    await this.addBackground(image ?? '', screenWidth, screenHeight, background);
  }

  private addBackground(
    image: string,
    screenWidth: number,
    screenHeight: number,
    background: string | undefined,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!isPlatformBrowser(AppComponent.PLATFORM_ID)) {
        return;
      }
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        return reject('Invalid canvas context');
      }
      canvas.width = screenWidth;
      canvas.height = screenHeight;
      let imageElement: HTMLImageElement | null = new Image();
      imageElement.src = image;
      imageElement.onload = () => {
        const gradients = background ? this.parseCSSGradientRule(background) : [];
        let contextGradient: CanvasGradient | undefined;
        if (gradients.length > 1) {
          contextGradient = context.createLinearGradient(0, 0, screenWidth, 0);
          gradients.forEach(({ color, movement }) => {
            contextGradient?.addColorStop(movement, color);
          });
        }
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        if (background) {
          context.fillStyle = contextGradient ?? background;
          context.fillRect(0, 0, canvas.width, canvas.height);
        }
        if (imageElement) {
          context.drawImage(imageElement, 0, 0);
        }
        const newImg = canvas.toDataURL('image/jpeg', RESOLUTION);
        URL.revokeObjectURL(image);
        this.photos.update((photos) => [...photos, newImg]);
        imageElement = null;
        resolve();
      };
      imageElement.onerror = () => {
        reject('Failed to load image for background addition.');
      };
    });
  }

  private parseCSSGradientRule(_gradient: string): { color: string; movement: number }[] {
    let gradient = _gradient;
    if (!gradient.includes('linear-gradient')) {
      return [];
    }
    gradient = gradient.replace('linear-gradient', '');
    gradient = gradient.substring(1); // removing first parentheses
    gradient = gradient.slice(0, -1); // removing last parentheses
    let gradientSplitted = gradient.split('(');
    gradient = gradientSplitted.join('{');
    gradientSplitted = gradient.split(')');
    gradient = gradientSplitted.join('}');

    gradient = gradient.replace(/({.*?})|,/g, (...m) => m[1] || '|'); // Replacing all commas outside {}
    gradientSplitted = gradient.split(', ');
    gradient = gradientSplitted.join(',');
    gradientSplitted = gradient.split(')');
    gradient = gradientSplitted.join('}');
    gradientSplitted = gradient.split('{');
    gradient = gradientSplitted.join('(');
    gradientSplitted = gradient.split('}');
    gradient = gradientSplitted.join(')');

    let gradientStops = gradient.split('|');
    gradientStops = gradientStops.splice(1, gradientStops.length);
    const gradients = gradientStops.map((gradientStop): { color: string; movement: number } => {
      let gradientStopArray = gradientStop.split(' ');
      gradientStopArray = gradientStopArray.splice(1, gradientStop.length);
      const color = gradientStopArray[0];
      const movement = +gradientStopArray[1].replace('%', '') / 100;
      return { color, movement };
    });
    return gradients;
  }

  public createGif(images: string[]): Promise<string> {
    return new Promise((resolve, reject) => {
      const { screenHeight, screenWidth } = this.sceneService?.sceneManager?.viewport ?? {
        screenHeight: 0,
        screenWidth: 0,
      };
      const options = {
        gifWidth: screenWidth,
        gifHeight: screenHeight,
        images,
        interval: 0.4,
      };
      return createGIF(options, (obj: { image: string; error?: string }) => {
        if (!obj.error) {
          const image = obj.image;
          this.photosCompleted.set(true);
          resolve(image);
        }
        reject(obj.error);
      });
    });
  }

  public async addPreciateFrame(
    photoBoothPictures: PhotoBoothPicture[],
    date: string,
    socialName: string,
    frameHeight = 75,
  ): Promise<FramedPicture[]> {
    const imagesWithFrame: FramedPicture[] = [];
    const imagesWithFrameBase64 = [];
    for (const image of photoBoothPictures) {
      // We only add frame to jpeg images.
      if (!image.url.includes('image/gif')) {
        try {
          const imageWithFrame = await this.createFinalImage(image, date, socialName, frameHeight, image.id);
          if (imageWithFrame) {
            imagesWithFrameBase64.push(`data:image/jpeg;base64,${imageWithFrame.base64}`);
            imagesWithFrame.push(imageWithFrame);
          }
        } catch (e) {
          this.error('Failed to add frame', e);
        }
      }
    }
    // we create a new GIF with the framed images.
    const gif = photoBoothPictures.find((p) => p.url.includes('image/gif'));
    if (gif) {
      const framedGif = await this.createGifWithFramedImages(imagesWithFrameBase64, gif.id);
      imagesWithFrame.push(framedGif);
    }
    return Promise.resolve(imagesWithFrame);
  }

  private createGifWithFramedImages(finalImagesBase64: string[], id: number): Promise<FramedPicture> {
    return new Promise((resolve) => {
      // Using void to explicitly mark that we're handling the promise inside the callback
      void this.createGif(finalImagesBase64).then((gif) => {
        const auxImage = new Image();
        auxImage.onload = () => {
          const block = gif.split(';');
          const type = block[0].split(':')[1];
          const base64 = block[1].split(',')[1];
          resolve({ base64, type, width: auxImage.width, height: auxImage.height, id });
        };
        auxImage.src = gif;
      });
    });
  }

  private createFinalImage(
    image: PhotoBoothPicture,
    date: string,
    socialName: string,
    frameHeight = 75,
    id: number,
  ): Promise<FramedPicture | undefined> {
    if (!isPlatformBrowser(AppComponent.PLATFORM_ID)) {
      return Promise.resolve(undefined);
    }
    return new Promise((resolve, reject) => {
      const auxImage = new Image();
      auxImage.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return reject('Invalid canvas context');
        }

        canvas.width = auxImage.width;
        canvas.height = auxImage.height + frameHeight;
        ctx.fillStyle = '#FFF';
        ctx.fillRect(0, auxImage.height, auxImage.width, frameHeight);
        ctx.font = FONT_FAMILY_BOLD;
        ctx.fillStyle = '#000';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.fillText(date, PHOTO_BOOTH_FRAME_PADDING, auxImage.height + frameHeight / 2);
        ctx.textAlign = 'center';
        ctx.fillText(socialName, auxImage.width / 2, auxImage.height + frameHeight / 2);
        const logo = new Image();
        logo.onload = () => {
          const logoHeight = frameHeight - PHOTO_BOOTH_FRAME_PADDING * 2; // Adding padding.
          const logoWidth = logoHeight * 4.25; // Proportion of the new logo (17:4)
          ctx.drawImage(
            logo,
            auxImage.width - logoWidth - PHOTO_BOOTH_FRAME_PADDING,
            auxImage.height + PHOTO_BOOTH_FRAME_PADDING,
            logoWidth,
            logoHeight,
          );
          ctx.drawImage(auxImage, 0, 0);
          const { height, width } = canvas;
          const imageURL = canvas.toDataURL('image/jpeg', RESOLUTION);
          const block = imageURL.split(';');
          const type = block[0].split(':')[1];
          const base64 = block[1].split(',')[1];
          resolve({ base64, type, width, height, id });
        };
        logo.src = 'assets/images/logos/scoot_logo_primary.png';
      };
      auxImage.src = image.url;
    });
  }

  public clearCountdown(): void {
    if (this.countdown) {
      clearInterval(this.countdown);
      this.countdown = undefined;
    }
  }

  public reset(): void {
    this.photosCompleted.set(false);
    this.photos.set([]);
    this.clearCountdown();
  }

  public removeBanner(): void {
    this.headerToastMessageService.removeMessage(this.bannerId);
    this.bannerId = undefined;
  }

  public destroy(): void {
    this.reset();
    if (this.countDownResolver) {
      this.countDownResolver(true);
      this.countDownResolver = undefined;
    }
    this.clearCountdown();
    this.boothStartedByUser.set(false);
    this.boothInSession.set(false);
  }

  /**
   * Converts the internal photo URLs to PhotoBoothPicture objects
   * @returns An array of PhotoBoothPicture objects
   */
  public getPhotoBoothPictures(): PhotoBoothPicture[] {
    return this.photos().map((url, index) => ({
      url,
      id: index,
      isGif: url.includes('image/gif'),
    }));
  }
}
